import { SortDirection } from "client/Api/common";

export function sortBy<TItem>(
	items: readonly TItem[],
	selector: (item: TItem) => unknown,
	direction: SortDirection = SortDirection.Ascending
): TItem[] {
	return items
		.map(
			(x, i) => ({
				item: x,
				v: selector(x),
				i
			})
		)
		.sort(
			(lhs, rhs) => {
				const lhsValue: unknown = lhs.v;
				const rhsValue: unknown = rhs.v;
				let result: number = lhs.i - rhs.i;

				if (lhsValue !== rhsValue) {
					if (typeof lhsValue === "undefined" || (lhsValue as any) > (rhsValue as any)) {
						result = 1;
					}
					if (typeof rhsValue === "undefined" || (lhsValue as any) < (rhsValue as any)) {
						result = -1;
					}

					if (direction === SortDirection.Descending) {
						result = result * -1;
					}
				}

				return result;
			}
		)
		.map((x) => x.item);
}

export function intersection<TItem>(
	a: readonly TItem[],
	b: readonly TItem[]
): TItem[] {
	const aUnique = new Set(a);
	return [...new Set(b)].filter(x => aUnique.has(x));
}

export function unique<TItem>(
	items: readonly TItem[],
	selector?: (item: TItem) => unknown
): TItem[] {
	if (!selector) {
		selector = x => x;
	}

	const foundSelectorValues = new Set<unknown>();

	const result: TItem[] = [];
	for (const item of items) {
		const selectorValue: unknown = selector(item);
		if (!foundSelectorValues.has(selectorValue)) {
			foundSelectorValues.add(selectorValue);
			result.push(item);
		}
	}

	return result;
}

export function union<TItem>(
	a: readonly TItem[],
	b: readonly TItem[]
): TItem[] {
	const foundItems = new Set<unknown>();

	const result: TItem[] = [];
	for (const item of a.concat(b)) {
		if (!foundItems.has(item)) {
			foundItems.add(item);
			result.push(item);
		}
	}

	return result;
}

export function without<TItem>(
	items: readonly TItem[],
	itemsToExclude: readonly TItem[]
): TItem[] {
	const bUnique = new Set(itemsToExclude);

	return items.filter(x => !bUnique.has(x));
}

export function groupBy<TItem, TSelectorValue extends string | number>(
	items: readonly TItem[],
	selector: (item: TItem) => TSelectorValue
): Partial<Record<TSelectorValue, TItem[]>> {
	const result: any = {};

	for (const item of items) {
		const selectorValue: TSelectorValue = selector(item);
		if (!result[selectorValue]) {
			result[selectorValue] = [];
		}

		result[selectorValue].push(item);
	}

	return result as unknown as Record<TSelectorValue, TItem[]>;
}

export function partition<TItem, TTrueItem extends TItem>(
	items: readonly TItem[],
	selector: (item: TItem) => item is TTrueItem
): [TTrueItem[], Exclude<TItem, TTrueItem>[]];
export function partition<TItem>(
	items: readonly TItem[],
	selector: (item: TItem) => boolean
): [TItem[], TItem[]];
export function partition<TItem>(
	items: readonly TItem[],
	selector: (item: TItem) => boolean
): [TItem[], TItem[]] {
	const groups: Partial<Record<1 | 0, TItem[]>> = groupBy<TItem, 1 | 0>(
		items,
		(item) => selector(item) ? 1 : 0
	);

	return [
		groups[1] ?? [],
		groups[0] ?? [],
	];
};

export function findLastIndex<TItem>(
	items: readonly TItem[],
	selector: (item: TItem) => boolean
): number {
	for (let i = items.length - 1; i >= 0; i--) {
		if (selector(items[i])) {
			return i;
		}
	}
	return -1;
}

export function min<TItem extends {}>(
	items: readonly TItem[]
): TItem | undefined {
	return items.reduce(
		(result: TItem | undefined, x: TItem) => {
			return result === undefined || x < result
				? x
				: result;
		},
		undefined
	);
}

export function max<TItem extends {}>(
	items: readonly TItem[]
): TItem | undefined {
	return items.reduce(
		(result: TItem | undefined, x: TItem) => {
			return result === undefined || x > result
				? x
				: result;
		},
		undefined
	);
}

export function zip<TItemA, TItemB>(
	a: readonly TItemA[],
	b: readonly TItemB[]
): [TItemA, TItemB][] {
	if (a.length !== b.length) {
		throw new Error("Arrays are not the same length");
	}
	const result: [TItemA, TItemB][] = [];
	for (let i = 0; i < a.length; i++) {
		result.push([a[i], b[i]]);
	}

	return result;
}

export function concat<TItem>(arrays: readonly (readonly TItem[])[]): readonly TItem[] {
	return ([] as TItem[]).concat(...arrays);
}

export function rotateLeft<T>(array: readonly T[], amount: number): T[] {
	amount = amount % array.length;
	const copy: T[] = [...array];
	const splicedItems: T[] = copy.splice(amount);
	splicedItems.push(...copy);
	return splicedItems;
}

export function sortNumbers(
	array: readonly number[],
	direction: SortDirection = SortDirection.Ascending
): readonly number[] {
	const compareFunction: (a: number, b: number) => number = direction === SortDirection.Ascending
		? (a, b) => a - b
		: (a, b) => b - a;

	return [...array].sort(compareFunction);
}

export function range(end: number): number[];
export function range(start: number, end: number): number[];
export function range(start: number, end?: number): number[] {
	if (typeof end === "undefined") {
		end = start;
		start = 0;
	}

	const step: number = end > start ? 1 : -1;

	const length: number = Math.max(
		Math.ceil(
			Math.abs(end - start)
		),
		0
	);

	const result = new Array(length);
	let currentValue: number = start;

	for (let i = 0; i < result.length; i++) {
		result[i] = currentValue;
		currentValue += step;
	}

	return result;
}

